import { useEffect, useMemo } from 'react'
import { toastManager } from '@systemeio/components'
import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next/pages'
import { CommunityOwnerBlockedError } from 'shared/errors/community-owner-blocked'
import { useInViewport } from 'shared/hooks/use-in-viewport'
import useUser from 'shared/hooks/use-user'
import type { PostInterface } from 'modules/community/types/post-interface'

interface PostAnnounceListResponse<T> {
  items: T[]
  hasMore?: boolean
}

interface UsePostAnnounceListParams<
  T extends PostInterface,
  ID = number | null,
  R extends PostAnnounceListResponse<T> = PostAnnounceListResponse<T>,
> {
  queryKey: readonly unknown[]
  fetchPosts: (lastPostId: ID) => Promise<{ data: R }>
  getNextPageId?: (data: R) => ID
  getHasMore?: (data: R) => boolean
  initialPageId?: ID
}

export function usePostAnnounceList<
  T extends PostInterface,
  ID = number | null,
  R extends PostAnnounceListResponse<T> = PostAnnounceListResponse<T>,
>({
  queryKey,
  fetchPosts,
  getNextPageId,
  getHasMore,
  initialPageId = null as ID,
}: UsePostAnnounceListParams<T, ID, R>): {
  posts: T[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  sentinelRef: React.RefCallback<HTMLElement | null>
  queryClient: ReturnType<typeof useQueryClient>
} {
  const { t } = useTranslation()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const { ref: sentinelRef, inViewport } = useInViewport()

  const infiniteQuery = useInfiniteQuery(
    infiniteQueryOptions({
      queryKey,
      queryFn: async ({ pageParam }) => {
        try {
          const response = await fetchPosts(pageParam as ID)
          return response.data
        } catch (error) {
          if (error instanceof CommunityOwnerBlockedError) {
            return { items: [] as T[], hasMore: false } as R
          }
          toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
          throw error
        }
      },
      getNextPageParam: lastPage => {
        const nextPage = getNextPageId
          ? getNextPageId(lastPage as R)
          : ((lastPage.items.at(-1)?.id as ID) ?? null)

        const hasMore = getHasMore
          ? getHasMore(lastPage as R)
          : (lastPage.hasMore ?? nextPage !== null)

        return hasMore ? nextPage : undefined
      },
      initialPageParam: initialPageId,
      enabled: Boolean(user),
      retry: (failureCount, error) => {
        if (error instanceof CommunityOwnerBlockedError) return false
        return failureCount < 3
      },
    }),
  )

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } = infiniteQuery

  const posts = useMemo(() => data?.pages.flatMap(page => page.items) ?? [], [data])

  useEffect(() => {
    if (inViewport && !isFetchingNextPage && hasNextPage && user) {
      fetchNextPage()
    }
  }, [inViewport, isFetchingNextPage, hasNextPage, user, fetchNextPage])

  return {
    posts,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage,
    hasNextPage,
    sentinelRef,
    queryClient,
  }
}
